import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lightbulb, Sparkle, Wand, Thermometer, Gauge, Fan, Zap } from 'lucide-react';

type Recommendation = {
  id: string;
  system: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  potentialSavings: string;
  details: string;
  icon: 'hvac' | 'chiller' | 'lighting' | 'power';
};

const EfficiencyRecommendationsCard = () => {
  const [activeRecommendation, setActiveRecommendation] = useState<string | null>(null);

  // Sample efficiency recommendations data based on load control, HVAC, and chillers
  const recommendations: Recommendation[] = [
    {
      id: 'rec1',
      system: 'Chiller Plant',
      description: 'Optimize chiller sequence operation to improve efficiency during partial loads',
      priority: 'high',
      potentialSavings: '14.2% energy reduction',
      details: 'Current chiller plant operation maintains all units at partial load. Implementing a sequence strategy where fewer chillers run at optimal efficiency will reduce energy consumption significantly during non-peak hours.',
      icon: 'chiller'
    },
    {
      id: 'rec2',
      system: 'HVAC Control',
      description: 'Implement demand-controlled ventilation in conference rooms and common areas',
      priority: 'medium',
      potentialSavings: '8.5% energy reduction',
      details: 'Installing CO2 sensors to regulate air exchange rates based on actual occupancy will prevent over-ventilation of spaces during periods of low use, reducing fan energy and heating/cooling loads.',
      icon: 'hvac'
    },
    {
      id: 'rec3',
      system: 'Lighting Systems',
      description: 'Replace remaining fluorescent fixtures with LED lighting in warehouse areas',
      priority: 'medium',
      potentialSavings: '11.3% lighting energy reduction',
      details: 'The warehouse area still uses 78 T8 fluorescent fixtures. Replacing these with LED equivalents will reduce lighting energy consumption while improving light quality and reducing maintenance.',
      icon: 'lighting'
    },
    {
      id: 'rec4',
      system: 'Chilled Water Reset',
      description: 'Implement dynamic chilled water temperature reset based on building load',
      priority: 'high',
      potentialSavings: '7.8% chiller energy reduction',
      details: 'Currently, chilled water setpoint remains fixed at 44°F. Implementing an automatic reset schedule that raises the setpoint during lower load conditions can improve chiller efficiency without impacting comfort.',
      icon: 'chiller'
    },
    {
      id: 'rec5',
      system: 'Power Factor Correction',
      description: 'Install power factor correction capacitors at main distribution panel',
      priority: 'low',
      potentialSavings: '3.5% reduction in utility penalties',
      details: 'Current power factor measurements show an average of 0.85. Installing correction capacitors will improve this to >0.95, reducing utility penalties and improving electrical system efficiency.',
      icon: 'power'
    }
  ];

  const toggleRecommendation = (recId: string) => {
    if (activeRecommendation === recId) {
      setActiveRecommendation(null);
    } else {
      setActiveRecommendation(recId);
    }
  };

  const getRecommendationIcon = (icon: string) => {
    switch (icon) {
      case 'hvac':
        return <Gauge className="h-4 w-4 text-blue-500" />;
      case 'chiller':
        return <Thermometer className="h-4 w-4 text-cyan-500" />;
      case 'lighting':
        return <Lightbulb className="h-4 w-4 text-amber-500" />;
      case 'power':
        return <Zap className="h-4 w-4 text-purple-500" />;
      default:
        return <Sparkle className="h-4 w-4 text-emerald-500" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl font-bold flex items-center">
          <Sparkle className="inline-block mr-2 h-5 w-5 text-amber-500" />
          Efficiency Recommendations
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendations.map(rec => (
            <Card 
              key={rec.id}
              className={`shadow-sm border transition-all ${
                activeRecommendation === rec.id
                  ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800'
                  : 'border-gray-200 dark:border-gray-800'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center">
                    {getRecommendationIcon(rec.icon)}
                    <span className="ml-2 font-medium text-gray-800 dark:text-white">
                      {rec.system}
                    </span>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    rec.priority === 'high' 
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      : rec.priority === 'medium'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  }`}>
                    {rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1)} Priority
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {rec.description}
                </p>
                
                <div className="flex justify-between items-center mt-3">
                  <div className="text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      Potential Savings: {rec.potentialSavings}
                    </span>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleRecommendation(rec.id)}
                    className="text-xs h-8"
                  >
                    {activeRecommendation === rec.id ? 'Hide Details' : 'View Details'}
                  </Button>
                </div>
                
                {activeRecommendation === rec.id && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                    <p>{rec.details}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default EfficiencyRecommendationsCard;
